import React, { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useClientFilter } from "@/context/ClientFilterContext";
import { useCardTableLabels } from "@/hooks/useCardTableLabels";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LayoutDashboard, Briefcase, ArrowUpDown, Receipt, Users, FileBarChart2, LogOut, Clock, CalendarDays, FileText, Menu, X, UsersRound, Settings, Palmtree, CalendarRange, Building2, TrendingUp, Coins, Boxes } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
  { to: "/projecten", key: "projects", icon: Briefcase, id: "nav-projecten" },
  { to: "/meerwerk", key: "variations", icon: ArrowUpDown, id: "nav-meerwerk", adminOnly: true },
  { to: "/weekstaat", key: "timesheet", icon: CalendarDays, id: "nav-weekstaat" },
  { to: "/werkrooster", key: "workSchedule", icon: CalendarRange, id: "nav-werkrooster" },
  { to: "/inventaris", key: "inventory", icon: Boxes, id: "nav-inventaris" },
  { to: "/uren", key: "hours", icon: Clock, id: "nav-uren" },
  { to: "/mijn-prijsafspraken", key: "myRates", icon: Coins, id: "nav-mijn-prijsafspraken", zzpOnly: true },
  { to: "/vakantieplanner", key: "leavePlanner", icon: Palmtree, id: "nav-vakantieplanner" },
  { to: "/inkoopfacturen", key: "purchaseInvoices", icon: Receipt, id: "nav-inkoopfacturen", adminOnly: true },
  { to: "/verkoopfacturen", key: "salesInvoices", icon: FileText, id: "nav-verkoopfacturen", adminOnly: true },
  { to: "/relaties", key: "relations", icon: Users, id: "nav-relaties", adminOnly: true },
  { to: "/rapportages", key: "reports", icon: FileBarChart2, id: "nav-rapportages", adminOnly: true },
  { to: "/marge", key: "marginReport", icon: TrendingUp, id: "nav-marge", adminOnly: true },
  { to: "/gebruikers", key: "users", icon: UsersRound, id: "nav-gebruikers", adminOnly: true },
  { to: "/instellingen", key: "settings", icon: Settings, id: "nav-instellingen", adminOnly: true },
];

export default function AppLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { client, clear } = useClientFilter();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useCardTableLabels();

  // Close mobile drawer when route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Filter nav items by role (memoized to avoid re-computation on every render)
  const visibleNav = useMemo(
    () => NAV_ITEMS.filter((item) => {
      if (item.adminOnly && user?.role !== "admin") return false;
      if (item.zzpOnly && user?.role !== "zzp") return false;
      return true;
    }),
    [user?.role]
  );

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F4F4F5]">
      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-zinc-200 z-40 flex items-center px-4 gap-3">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 rounded-sm hover:bg-zinc-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label={t("nav.openMenu")}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 bg-white border border-zinc-200 flex items-center justify-center rounded-sm overflow-hidden shrink-0">
            <img src="/logo.webp" alt={t("app.name")} className="w-full h-full object-cover scale-150" />
          </div>
          <div className="font-display font-bold text-base leading-tight truncate">{t("app.name")}</div>
        </div>
        <NotificationBell />
      </div>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`bg-white border-r border-zinc-200 flex flex-col fixed top-0 left-0 h-screen z-50 transition-transform duration-200 ease-out w-72 lg:w-64 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white border border-zinc-200 flex items-center justify-center rounded-sm overflow-hidden shrink-0">
              <img src="/logo.webp" alt={t("app.name")} className="w-full h-full object-cover scale-150" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-base leading-tight truncate">{t("app.name")}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{t("app.tagline")}</div>
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-2 -mr-2 rounded-sm hover:bg-zinc-100"
            aria-label={t("nav.closeMenu")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-3 pt-3">
          <GlobalSearch />
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-sm text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-[#0052FF] text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] label-caps text-zinc-500">{t("common.language")}</div>
            <div className="flex items-center gap-1">
              <div className="hidden lg:block"><NotificationBell placement="up" /></div>
              <LanguageSwitcher />
            </div>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-zinc-900 text-white rounded-sm flex items-center justify-center font-semibold text-sm shrink-0">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name || t("nav.user")}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={doLogout} className="w-full rounded-sm" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 lg:ml-64 min-h-screen pt-14 lg:pt-0">
        {client && (
          <div
            className="sticky top-14 lg:top-0 z-30 bg-amber-50 border-b border-amber-200 px-4 lg:px-8 py-2 flex items-center gap-3 text-sm"
            data-testid="global-client-breadcrumb"
          >
            <Building2 className="w-4 h-4 text-amber-800 shrink-0" />
            <span className="text-amber-900 truncate">
              <span className="text-amber-700">{t("dashboard.filteringOn")}:</span>{" "}
              <button
                type="button"
                onClick={() => navigate(`/opdrachtgever/${client.id}`)}
                className="font-semibold underline decoration-dotted underline-offset-2 hover:text-amber-950"
                data-testid="global-client-breadcrumb-name"
              >
                {client.name}
              </button>
            </span>
            <button
              type="button"
              onClick={clear}
              className="ml-auto p-1 rounded-sm hover:bg-amber-100 text-amber-800 shrink-0"
              aria-label={t("common.clear")}
              data-testid="global-client-breadcrumb-clear"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
